import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useFireOncePerUser } from '@/hooks/useFireOncePerUser';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useAhaFlag } from '@/hooks/useAhaFlag';
import { fireConfettiBurst, magicMomentStorageKey } from '@/lib/magic_moments';
import { useSessionStore } from '@/store/session';

// PR-UX-08 molecule · single responsibility: render Iron's killed candidate.
// Visual contract: vermilion border + strike-through candidate label + reason quote.
// Per cockpit/PAGE_STATES.md `session_running` substate: Iron's [KILL: ...] block.
//
// aha_forge v1 P3 magic moment: when this is the first iron-kill the user
// has ever seen, fire `magic_moment_iron_kill_first_seen_fired` once and
// emit a confetti burst (motion-respecting). ADDENDUM correction 3 puts the
// "first kill in session" detection in the parent (Live.tsx) so this stays
// prop-driven.
interface IronKillChipProps {
  candidateId: string;        // e.g. "candidate_b" (canonical from openapi.fyd.v1.yaml)
  killReason: string;         // Iron's quote (wire field: reason)
  monotonicId?: number;       // SSE event id (debug)
  onCite?: () => void;        // optional: jump to candidate B in report
  /** True if this chip is the first iron-kill in the current session AND
   *  the user has never seen one before (parent computes). */
  isFirstSeenInSession?: boolean;
}

export function IronKillChip({
  candidateId,
  killReason,
  monotonicId,
  onCite,
  isFirstSeenInSession,
}: IronKillChipProps) {
  const { t } = useTranslation();
  const letter = candidateLetter(candidateId);
  const ref = useRef<HTMLDivElement | null>(null);
  const reducedMotion = useReducedMotion();
  const magicMomentsEnabled = useAhaFlag('FYD_AHA_MAGIC_MOMENTS');
  const sessionId = useSessionStore((s) => s.current.session_id);
  const sessionStartedAt = useSessionStore((s) => s.current.started_at);

  const { fired, fire } = useFireOncePerUser(
    'magic_moment_iron_kill_first_seen_fired',
    magicMomentStorageKey('iron_kill_first_seen'),
  );

  useEffect(() => {
    if (!magicMomentsEnabled) return;
    if (!isFirstSeenInSession) return;
    if (fired) return;
    fire({
      user_id: 'auth',
      timestamp: new Date().toISOString(),
      session_id: sessionId,
      iron_kill_count_in_session: 1,
      ttfv_ms_from_session_start: sessionStartedAt
        ? Date.now() - new Date(sessionStartedAt).getTime()
        : null,
    });
    void fireConfettiBurst(ref.current, { reducedMotion });
  }, [
    magicMomentsEnabled,
    isFirstSeenInSession,
    fired,
    fire,
    sessionId,
    sessionStartedAt,
    reducedMotion,
  ]);

  const animateClass =
    magicMomentsEnabled && isFirstSeenInSession && !reducedMotion
      ? ' aha-iron-kill-pulse'
      : '';

  return (
    <div
      ref={ref}
      className={`inline-flex flex-col gap-1 px-3 py-2 border border-vermilion rounded-md bg-vermilion-50/30${animateClass}`}
      data-testid="iron-kill-chip"
      data-monotonic-id={monotonicId}
    >
      <div className="flex items-center gap-2 text-xs font-mono tracking-hud">
        <span className="text-vermilion-700 font-semibold">{t('ironKill.label')}</span>
        <span className="text-ink-2">→</span>
        <button
          type="button"
          onClick={onCite}
          className="text-ink line-through decoration-vermilion decoration-2 hover:text-vermilion-700 disabled:cursor-default"
          disabled={!onCite}
        >
          {t('ironKill.candidate', { letter })}
        </button>
      </div>
      <p className="text-sm text-ink-2 italic line-clamp-3">
        — {killReason}
      </p>
    </div>
  );
}

function candidateLetter(id: string): string {
  // candidate_b → "B"
  const m = id.match(/^candidate_([a-z])$/i);
  if (m) return m[1].toUpperCase();
  // fallback: last segment uppercased
  const parts = id.split('_');
  return (parts[parts.length - 1] || id).toUpperCase();
}

// Usage example (rendered by SessionLive route per FE_SPEC sse_client dispatch):
//
//   sessionStore.iron_kills.map(k => <IronKillChip key={k.monotonic_id} {...k} />)
export function IronKillStack({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-2 my-3">{children}</div>;
}
